import type { Cenario, Parametros } from '@favo/core';

import { CascataPreco } from '@/componentes/cascata-preco';
import { Celula } from '@/componentes/celula';
import { Dre, LinhaDre } from '@/componentes/linha-dre';
import { fatiasDoPreco, lancamentosDoProjeto } from '@/componentes/memoria-de-calculo';
import { Valor } from '@/componentes/valor';
import { formatarMoeda, formatarNumero } from '@/design/formato';

import { faltaPreencher } from './frases';

/* A MEMÓRIA DE CÁLCULO
 *
 * O laudo AFIRMAVA que o preço não cobre os custos e nunca MOSTRAVA para onde
 * vai cada real. Era uma tese sem prova: quem discordasse do veredito não tinha
 * onde conferir, e quem concordasse não tinha o que levar para a mesa.
 *
 * Estes dois capítulos são a evidência, e vêm logo depois da capa justamente
 * por isso: prova imediata depois da tese, antes de qualquer projeção.
 *
 * A derivação sai de `componentes/memoria-de-calculo`, a MESMA que o painel
 * usa. Recalcular aqui seria repetir a duplicação de motor que fez o dashboard
 * e o PDF divergirem na versão anterior, e desta vez dentro do documento que o
 * integrador imprime.
 */

/** Para onde vai cada real do preço praticado. */
export function CascataDoLaudo({ p, atual }: { p: Parametros; atual: Cenario | null }) {
  if (!atual) {
    return (
      <Celula como="section" className="secao">
        <h2 className="titulo-cartao titulo-secao">Para onde vai cada real</h2>
        <p className="apoio">{faltaPreencher(p)}</p>
      </Celula>
    );
  }

  const { fatias, margemPositiva, custoTotal } = fatiasDoPreco(p, atual);

  return (
    <Celula como="section" className="secao">
      {/* O título carrega o NÚMERO e a origem dele. No painel o preço está
          duas dobras acima e a ambiguidade não existe; aqui o capítulo fica ao
          lado de um veredito que recomenda OUTRO preço, e "cada real do preço"
          sem dizer qual preço é ambíguo no documento que vai à mesa. */}
      <h2 className="titulo-cartao titulo-secao">
        Para onde vai cada real de {formatarMoeda(p.ticket)}, preço praticado
      </h2>
      <p className="apoio">
        {margemPositiva
          ? 'As fatias somam exatamente o preço. A última não é sobra de cálculo: é a margem de contribuição, e é dela que sai a estrutura da empresa.'
          : `O custo passou do preço. A leitura abaixo é sobre o custo total de ${formatarMoeda(custoTotal)}, não sobre o preço.`}
      </p>
      {/* Sem o favo no papel: cem hexágonos custam tinta e não sobrevivem a
          uma impressão em preto e branco. A tabela de fatias carrega o mesmo
          número, e é ela que o leitor confere linha a linha. */}
      <CascataPreco fatias={fatias} favo={false} />
    </Celula>
  );
}

/** O demonstrativo, do preço ao resultado, por projeto e por mês. */
export function DreDoLaudo({
  p,
  atual,
  semPreco,
}: {
  p: Parametros;
  atual: Cenario | null;
  semPreco: boolean;
}) {
  const linhas = lancamentosDoProjeto(p, atual, semPreco);

  return (
    <Celula como="section" className="secao">
      <h2 className="titulo-cartao titulo-secao">Demonstrativo do projeto</h2>
      <p className="apoio">
        A coluna do mês é a do projeto multiplicada por{' '}
        {formatarNumero(p.V12 / 12, { casasMax: 1 })} projetos, o volume médio mensal considerado.
      </p>
      <Dre>
        {linhas.map((l) => (
          <LinhaDre
            key={l.nome}
            nome={l.nome}
            {...(l.nota === undefined ? {} : { nota: l.nota })}
            {...(l.operador === undefined ? {} : { operador: l.operador })}
            {...(l.campo === undefined ? {} : { campo: l.campo })}
            {...(l.total === undefined ? {} : { total: l.total })}
            {...(l.perda === undefined ? {} : { perda: l.perda })}
            valor={<Valor valor={l.projeto} formato="moeda" tamanho="apoio" />}
            percentual={<Valor valor={l.mes} formato="moeda" tamanho="nota" tom="apagado" />}
          />
        ))}
      </Dre>
    </Celula>
  );
}
